
import { NavLink } from "react-router-dom";
import { 
  Home, 
  ShoppingCart, 
  Utensils, 
  Truck, 
  Navigation 
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessRoute, AppRole } from "@/lib/permissions";

const navItems = [
  {
    title: "Dashboard",
    href: "/admin/dashboard",
    icon: Home,
    roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff'] as AppRole[]
  },
  {
    title: "Orders",
    href: "/admin/orders",
    icon: ShoppingCart,
    roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'kitchen', 'customer_service', 'dispatcher'] as AppRole[]
  },
  {
    title: "Kitchen",
    href: "/admin/kitchen-display",
    icon: Utensils,
    roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'kitchen'] as AppRole[]
  },
  {
    title: "Dispatch",
    href: "/admin/dispatcher",
    icon: Truck,
    roles: ['super_admin', 'admin', 'branch_admin', 'dispatcher'] as AppRole[]
  },
  {
    title: "Rider",
    href: "/admin/rider-view",
    icon: Navigation,
    roles: ['super_admin', 'admin', 'dispatcher', 'rider'] as AppRole[]
  }
];

export const AdminBottomNav = () => {
  const { userRoles, isAdmin, user } = useAuth();
  
  const currentRoles = (userRoles?.map(r => r.role) || []) as AppRole[];
  const isRiderOnly = currentRoles.includes('rider') && !isAdmin;
  
  // Filter items based on roles and permissions
  const visibleItems = navItems.filter(item => {
    // Admins/Super admins can see everything
    if (isAdmin) return true;
    
    // Special case for riders
    if (isRiderOnly && item.title === "Rider") return true;
    
    // Check if any of user's roles are in the item's allowed roles
    const hasAllowedRole = item.roles.some(role => currentRoles.includes(role));
    if (hasAllowedRole) return true;

    // Direct permission check based on route
    return canAccessRoute(currentRoles, item.href);
  }).map(item => {
    // Dynamically change Rider link for actual riders
    if (item.title === "Rider" && isRiderOnly) {
      return { ...item, href: "/rider/dashboard" };
    }
    return item;
  });

  if (visibleItems.length === 0) return null;

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 px-2 pb-safe shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
      <div className="flex justify-around items-center h-16">
        {visibleItems.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={({ isActive }) => 
              cn(
                "flex flex-col items-center justify-center flex-1 min-w-0 transition-all duration-200",
                isActive 
                  ? "text-primary scale-110 font-bold" 
                  : "text-gray-400 hover:text-gray-600"
              )
            }
          >
            <item.icon className={cn("h-5 w-5 mb-1", "transition-transform duration-200")} />
            <span className="text-[10px] xs:text-xs truncate w-full text-center px-1">
              {item.title}
            </span>
            {/* Active Indicator Bar */}
            <div className={cn(
              "h-1 w-8 rounded-full mt-0.5 transition-all duration-200",
              "bg-primary",
              "opacity-0",
              "translate-y-1"
            )} />
          </NavLink>
        ))}
      </div>
    </nav>
  );
};
